import React, { useState } from 'react';
import { uploadMenuImages, deleteMenuImage } from '../../../utils/storage';
import './MenuItemForm.css';

const MenuItemForm = ({ item, categories, onSave, onCancel, saving = false }) => {
  const [formData, setFormData] = useState({
    name: item?.name || '',
    category: item?.category || '',
    description: item?.description || '',
    price: item?.price || '',
    cookingTime: item?.cookingTime || '',
    tags: item?.tags || [],
    isAvailable: item?.isAvailable !== undefined ? item.isAvailable : true,
    isFeatured: item?.isFeatured || false,
    images: item?.images || [],
    customizations: item?.customizations || {
      sizes: [],
      spiceLevels: [],
      addons: []
    }
  });
  const [errors, setErrors] = useState({});
  const [charCount, setCharCount] = useState(item?.description?.length || 0);
  const [uploading, setUploading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (name === 'description') {
      setCharCount(value.length);
    }
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleTagToggle = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter(t => t !== tag)
        : [...prev.tags, tag]
    }));
  };

  const handleAddSize = () => {
    setFormData(prev => ({
      ...prev,
      customizations: {
        ...prev.customizations,
        sizes: [...prev.customizations.sizes, { name: '', price: '' }]
      }
    }));
  };

  const handleRemoveSize = (index) => {
    setFormData(prev => ({
      ...prev,
      customizations: {
        ...prev.customizations,
        sizes: prev.customizations.sizes.filter((_, i) => i !== index)
      }
    }));
  };

  const handleSizeChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      customizations: {
        ...prev.customizations,
        sizes: prev.customizations.sizes.map((size, i) =>
          i === index ? { ...size, [field]: value } : size
        )
      }
    }));
  };

  const handleAddAddon = () => {
    setFormData(prev => ({
      ...prev,
      customizations: {
        ...prev.customizations,
        addons: [...prev.customizations.addons, { name: '', price: '' }]
      }
    }));
  };

  const handleRemoveAddon = (index) => {
    setFormData(prev => ({
      ...prev,
      customizations: {
        ...prev.customizations,
        addons: prev.customizations.addons.filter((_, i) => i !== index)
      }
    }));
  };

  const handleAddonChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      customizations: {
        ...prev.customizations,
        addons: prev.customizations.addons.map((addon, i) =>
          i === index ? { ...addon, [field]: value } : addon
        )
      }
    }));
  };

  const handleSpiceLevelToggle = (level) => {
    setFormData(prev => ({
      ...prev,
      customizations: {
        ...prev.customizations,
        spiceLevels: prev.customizations.spiceLevels.includes(level)
          ? prev.customizations.spiceLevels.filter(l => l !== level)
          : [...prev.customizations.spiceLevels, level]
      }
    }));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (formData.images.length + files.length > 5) {
      alert('Maximum 5 images allowed');
      return;
    }

    try {
      setUploading(true);
      const uploadedUrls = await uploadMenuImages(files);
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...uploadedUrls]
      }));
    } catch (error) {
      console.error('Error uploading images:', error);
      alert('Failed to upload images. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = async (index) => {
    const imageUrl = formData.images[index];
    
    try {
      await deleteMenuImage(imageUrl);
      setFormData(prev => ({
        ...prev,
        images: prev.images.filter((_, i) => i !== index)
      }));
    } catch (error) {
      console.error('Error deleting image:', error);
      // Still remove from form even if delete fails
      setFormData(prev => ({
        ...prev,
        images: prev.images.filter((_, i) => i !== index)
      }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Item name is required';
    if (!formData.category) newErrors.category = 'Category is required';
    if (!formData.price || parseFloat(formData.price) <= 0) {
      newErrors.price = 'Valid price is required';
    }
    if (formData.description.length > 500) {
      newErrors.description = 'Description must be 500 characters or less';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }

    const itemData = {
      ...formData,
      price: parseFloat(formData.price),
      cookingTime: formData.cookingTime ? parseInt(formData.cookingTime) : null,
      isPopular: formData.tags.includes('⭐ Popular'),
      isNew: formData.tags.includes('🆕 New')
    };

    onSave(itemData);
  };

  const availableTags = ['🌶️ Spicy', '⭐ Popular', '🥬 Veg', '🆕 New', '🏆 Best Seller'];

  return (
    <form onSubmit={handleSubmit} className="menu-item-form">
      <div className="form-columns">
        <div className="form-left">
          <div className="form-group">
            <label className="form-label">
              Item Name <span className="required">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`form-input ${errors.name ? 'error' : ''}`}
              placeholder="Enter item name"
            />
            {errors.name && <span className="error-message">{errors.name}</span>}
          </div>

          <div className="form-group">
            <label className="form-label">
              Category <span className="required">*</span>
            </label>
            <select
              name="category"
              value={formData.category}
              onChange={handleChange}
              className={`form-input ${errors.category ? 'error' : ''}`}
            >
              <option value="">Select category</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            {errors.category && <span className="error-message">{errors.category}</span>}
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              className={`form-textarea ${errors.description ? 'error' : ''}`}
              placeholder="Describe your item..."
              rows="5"
            />
            <div className="char-counter">
              {charCount}/500
            </div>
            {errors.description && <span className="error-message">{errors.description}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                Price (PKR) <span className="required">*</span>
              </label>
              <div className="input-with-prefix">
                <span className="input-prefix">PKR</span>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  className={`form-input ${errors.price ? 'error' : ''}`}
                  placeholder="0"
                  min="0"
                  step="0.01"
                />
              </div>
              {errors.price && <span className="error-message">{errors.price}</span>}
            </div>

            <div className="form-group">
              <label className="form-label">Cooking Time (minutes)</label>
              <div className="input-with-suffix">
                <input
                  type="number"
                  name="cookingTime"
                  value={formData.cookingTime}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="0"
                  min="0"
                />
                <span className="input-suffix">mins</span>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Tags</label>
            <div className="tags-checkboxes">
              {availableTags.map(tag => (
                <label key={tag} className="tag-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.tags.includes(tag)}
                    onChange={() => handleTagToggle(tag)}
                  />
                  <span className="checkmark"></span>
                  {tag}
                </label>
              ))}
            </div>
          </div>

          <div className="customizations-section">
            <h3 className="section-title">Customization Options</h3>

            <div className="customization-group">
              <div className="group-header">
                <label className="group-label">Size Options</label>
                <button type="button" className="add-option-btn" onClick={handleAddSize}>
                  + Add Size
                </button>
              </div>
              {formData.customizations.sizes.map((size, index) => (
                <div key={index} className="option-row">
                  <input
                    type="text"
                    placeholder="Size name"
                    value={size.name}
                    onChange={(e) => handleSizeChange(index, 'name', e.target.value)}
                    className="option-input size-name-input"
                  />
                  <div className="input-with-prefix size-price-input">
                    <span className="input-prefix">PKR</span>
                    <input
                      type="number"
                      placeholder="Price"
                      value={size.price}
                      onChange={(e) => handleSizeChange(index, 'price', e.target.value)}
                      className="option-input"
                    />
                  </div>
                  <button
                    type="button"
                    className="remove-option-btn"
                    onClick={() => handleRemoveSize(index)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>

            <div className="customization-group">
              <label className="group-label">Spice Levels</label>
              <div className="spice-levels">
                {['Mild', 'Medium', 'Spicy', 'Extra Spicy'].map(level => (
                  <label key={level} className="tag-checkbox">
                    <input
                      type="checkbox"
                      checked={formData.customizations.spiceLevels.includes(level)}
                      onChange={() => handleSpiceLevelToggle(level)}
                    />
                    <span className="checkmark"></span>
                    {level}
                  </label>
                ))}
              </div>
            </div>

            <div className="customization-group">
              <div className="group-header">
                <label className="group-label">Add-ons</label>
                <button type="button" className="add-option-btn" onClick={handleAddAddon}>
                  + Add Add-on
                </button>
              </div>
              {formData.customizations.addons.map((addon, index) => (
                <div key={index} className="option-row">
                  <input
                    type="text"
                    placeholder="Add-on name"
                    value={addon.name}
                    onChange={(e) => handleAddonChange(index, 'name', e.target.value)}
                    className="option-input addon-name-input"
                  />
                  <div className="input-with-prefix addon-price-input">
                    <span className="input-prefix">PKR</span>
                    <input
                      type="number"
                      placeholder="Price"
                      value={addon.price}
                      onChange={(e) => handleAddonChange(index, 'price', e.target.value)}
                      className="option-input"
                    />
                  </div>
                  <button
                    type="button"
                    className="remove-option-btn"
                    onClick={() => handleRemoveAddon(index)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="form-right">
          <div className="form-group">
            <label className="form-label">
              Item Photos <span className="required">*</span>
            </label>
            <div 
              className="upload-area" 
              onClick={() => document.getElementById('image-upload').click()}
            >
              <input
                id="image-upload"
                type="file"
                multiple
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
                disabled={uploading}
              />
              {uploading ? (
                <div className="uploading-state">
                  <div className="spinner"></div>
                  <p>Uploading images...</p>
                </div>
              ) : (
                <>
                  <span className="upload-icon">📷</span>
                  <p className="upload-text">Click to upload or drag and drop</p>
                  <p className="upload-subtext">Max 5 photos, up to 5MB each</p>
                </>
              )}
            </div>
            {formData.images.length > 0 && (
              <div className="image-previews">
                {formData.images.map((img, index) => (
                  <div key={index} className="image-preview">
                    <img src={img} alt={`Preview ${index + 1}`} />
                    <button
                      type="button"
                      className="remove-image-btn"
                      onClick={() => handleRemoveImage(index)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Availability</label>
            <div className="toggle-wrapper">
              <label className="toggle-switch-large">
                <input
                  type="checkbox"
                  name="isAvailable"
                  checked={formData.isAvailable}
                  onChange={handleChange}
                />
                <span className="toggle-slider-large"></span>
              </label>
              <span className="toggle-label">
                {formData.isAvailable ? 'Available' : 'Sold Out'}
              </span>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Feature on Homepage</label>
            <label className="checkbox-label-large">
              <input
                type="checkbox"
                name="isFeatured"
                checked={formData.isFeatured}
                onChange={handleChange}
              />
              <span className="checkmark-large"></span>
              Show this item on homepage
            </label>
          </div>
        </div>
      </div>

      <div className="form-footer">
        <button type="button" className="cancel-btn" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="save-btn" disabled={uploading || saving}>
          {saving ? 'Saving...' : uploading ? 'Uploading...' : item ? 'Update Item' : 'Save Item'}
        </button>
      </div>
    </form>
  );
};

export default MenuItemForm;

